import axios from 'axios';
import {
  AvailableAddOnDto,
  SubscriptionAddOnDto,
  AddSubscriptionAddOnRequest,
} from '../types/subscription';
import { API_BASE_URL } from '../config/api';

const API_VERSION = 'v1.0';

/**
 * Service for managing subscription add-ons
 */
class SubscriptionAddOnService {
  private getHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get all available add-ons for the current user's subscription
   */
  async getAvailableAddOns(): Promise<AvailableAddOnDto[]> {
    // Add cache-busting timestamp to ensure fresh data
    const response = await axios.get(
      `${API_BASE_URL}/api/${API_VERSION}/platform-subscriptions/add-ons/available`,
      {
        headers: this.getHeaders(),
        params: { _t: Date.now() }
      }
    );
    return response.data;
  }

  /**
   * Get all add-ons for a specific subscription
   */
  async getSubscriptionAddOns(subscriptionId: number): Promise<SubscriptionAddOnDto[]> {
    const response = await axios.get(
      `${API_BASE_URL}/api/${API_VERSION}/platform-subscriptions/${subscriptionId}/add-ons`,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  /**
   * Add an add-on to a subscription
   */
  async addSubscriptionAddOn(
    subscriptionId: number,
    request: AddSubscriptionAddOnRequest
  ): Promise<SubscriptionAddOnDto> {
    const response = await axios.post(
      `${API_BASE_URL}/api/${API_VERSION}/platform-subscriptions/${subscriptionId}/add-ons`,
      request,
      { headers: this.getHeaders() }
    );

    // Note: Don't dispatch events here - let the caller dispatch after updating local state
    // to avoid race conditions where hooks fetch stale data before state is updated

    return response.data;
  }

  /**
   * Cancel a subscription add-on
   */
  async cancelSubscriptionAddOn(
    subscriptionId: number,
    addOnId: number
  ): Promise<SubscriptionAddOnDto> {
    const response = await axios.delete(
      `${API_BASE_URL}/api/${API_VERSION}/platform-subscriptions/${subscriptionId}/add-ons/${addOnId}`,
      { headers: this.getHeaders() }
    );

    // Note: Don't dispatch events here - let the caller dispatch after updating local state
    // to avoid race conditions where hooks fetch stale data before state is updated

    return response.data;
  }

  /**
   * Reactivate a cancelled subscription add-on
   */
  async reactivateSubscriptionAddOn(
    subscriptionId: number,
    addOnId: number
  ): Promise<SubscriptionAddOnDto> {
    const response = await axios.post(
      `${API_BASE_URL}/api/${API_VERSION}/platform-subscriptions/${subscriptionId}/add-ons/${addOnId}/reactivate`,
      {},
      { headers: this.getHeaders() }
    );

    // Note: Don't dispatch events here - let the caller dispatch after updating local state
    // to avoid race conditions where hooks fetch stale data before state is updated

    return response.data;
  }
}

const subscriptionAddOnService = new SubscriptionAddOnService();
export { subscriptionAddOnService };
export default subscriptionAddOnService;
